import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CollaborativeMap, SharedPackingList, AppUser, CampingSpot, GpxRoute } from '../../types';
import {
  createCollaborativeMap,
  addSpotToMap,
  removeSpotFromMap,
  addRouteToMap,
  removeRouteFromMap,
  addPackingListToMap,
  removePackingListFromMap,
  updatePackingListInMap,
  inviteUserByEmail,
  sendEmailInvite,
  subscribeToPendingInvites,
  hasUnreadCollabChat,
  leaveOrDeleteMap,
  removeMemberFromMap,
} from '../../services/collaborativeMapsService';
import { fetchSpotsByIds } from '../../services/firebaseService';
import { loadPackingLists } from '../mycontent/packingStorage';
import { buildSpotLocationLabels } from '../../utils/spotLocationLabel';
import CollabChatPanel from './CollabChatPanel';
import type { PackingList } from '../mycontent/packingTypes';
import { useTranslation } from 'react-i18next';
import {
  X,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  MapPin,
  Users,
  Mail,
  MoreVertical,
  Plus,
  Search,
  Check,
  LogOut,
  Trash2,
  Copy,
  Route,
  Map as MapIcon,
  Lock,
  ClipboardList,
  MessageCircle,
} from 'lucide-react';

interface CollaborativeMapModalProps {
  mapId: string | null;
  maps: CollaborativeMap[];
  spots: CampingSpot[];
  routes?: GpxRoute[];
  currentUser: AppUser;
  isPaidUser: boolean;
  addToMapSpotId?: string | null;
  addToMapRouteId?: string | null;
  listMode?: boolean;
  /** Render inline (no modal wrapper/backdrop) for embedding in panels */
  inline?: boolean;
  onClose: () => void;
  onMapCreated?: (mapId: string) => void;
  onSelectSpot?: (spot: CampingSpot, fromMapId: string) => void;
  onSelectRoute?: (routeId: string, fromMapId: string) => void;
}

const CollaborativeMapModal: React.FC<CollaborativeMapModalProps> = ({
  mapId,
  maps,
  spots,
  routes = [],
  currentUser,
  isPaidUser,
  addToMapSpotId,
  addToMapRouteId,
  listMode = false,
  inline = false,
  onClose,
  onMapCreated,
  onSelectSpot,
  onSelectRoute,
}) => {
  const { t, i18n } = useTranslation();
  // Internal navigation state for list mode
  const [internalMapId, setInternalMapId] = useState<string | null>(mapId);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Sync internalMapId when mapId prop changes
  useEffect(() => { setInternalMapId(mapId); }, [mapId]);

  const map = maps.find(m => m.id === internalMapId) ?? null;

  // Ventende invitasjoner (e-poster uten konto ennå) for gjeldende kart
  useEffect(() => {
    if (!internalMapId) { setPendingInvites([]); return; }
    const unsub = subscribeToPendingInvites(internalMapId, setPendingInvites);
    return unsub;
  }, [internalMapId]);

  const isPickMapMode = (!!addToMapSpotId || !!addToMapRouteId) && !mapId;
  const isListMode = listMode && !internalMapId && !showCreateForm && !isPickMapMode;
  const isCreateMode = showCreateForm || (!internalMapId && !map && !isPickMapMode && !listMode);

  // Create mode state
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // View mode state
  const [spotSearch, setSpotSearch] = useState('');
  const [showSpotSearch, setShowSpotSearch] = useState(false);
  const [routeSearch, setRouteSearch] = useState('');
  const [showRouteSearch, setShowRouteSearch] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'loading' | 'ok' | 'not_found' | 'sending_email' | 'invited' | 'already_member' | 'error'>('idle');
  const [pendingInvites, setPendingInvites] = useState<string[]>([]);
  const [showChat, setShowChat] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [selectedMapForSpot, setSelectedMapForSpot] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [addedToMap, setAddedToMap] = useState<string | null>(null);
  const [showCreateInPick, setShowCreateInPick] = useState(false);
  const [showPackingPicker, setShowPackingPicker] = useState(false);
  const [expandedPackingId, setExpandedPackingId] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const id = await createCollaborativeMap(currentUser, newName.trim(), newDesc.trim());
      if (isPickMapMode) {
        if (addToMapSpotId) {
          await addSpotToMap(id, addToMapSpotId).catch(console.error);
        } else if (addToMapRouteId) {
          await addRouteToMap(id, addToMapRouteId).catch(console.error);
        }
        setAddedToMap(id);
        setTimeout(() => onClose(), 800);
      } else if (listMode) {
        // Created from list mode — navigate into the new map
        setShowCreateForm(false);
        setNewName('');
        setNewDesc('');
        setInternalMapId(id);
      } else {
        if (onMapCreated) onMapCreated(id);
        onClose();
      }
    } catch (err) {
      console.error('Kunne ikke opprette mappe', err);
    } finally {
      setCreating(false);
    }
  };

  const handleAddSpot = async (spotId: string) => {
    if (!map) return;
    await addSpotToMap(map.id, spotId).catch(console.error);
    setShowSpotSearch(false);
    setSpotSearch('');
  };

  const handleRemoveSpot = async (spotId: string) => {
    if (!map) return;
    await removeSpotFromMap(map.id, spotId).catch(console.error);
  };

  const handleAddRoute = async (routeId: string) => {
    if (!map) return;
    await addRouteToMap(map.id, routeId).catch(console.error);
    setShowRouteSearch(false);
    setRouteSearch('');
  };

  const handleRemoveRoute = async (routeId: string) => {
    if (!map) return;
    await removeRouteFromMap(map.id, routeId).catch(console.error);
  };

  const handleInvite = async () => {
    if (!map || !inviteEmail.trim()) return;
    setInviteStatus('loading');
    const result = await inviteUserByEmail(map.id, inviteEmail.trim());
    setInviteStatus(result);
    if (result === 'ok') {
      setInviteEmail('');
      setTimeout(() => setInviteStatus('idle'), 4000);
    }
    // 'not_found' → behold e-posten og vis bekreftelses-prompt for e-postinvitasjon
  };

  const handleSendEmailInvite = async () => {
    if (!map || !inviteEmail.trim()) return;
    setInviteStatus('sending_email');
    const result = await sendEmailInvite(map.id, inviteEmail.trim(), {
      uid: currentUser.uid,
      displayName: currentUser.displayName || currentUser.email,
      lang: i18n.language?.split('-')[0],
    });
    setInviteStatus(result);
    if (result === 'invited') {
      setInviteEmail('');
      setTimeout(() => setInviteStatus('idle'), 4000);
    }
  };

  const handleCopyLink = () => {
    if (!map) return;
    const url = `${window.location.origin}${window.location.pathname}?collab=${map.id}&token=${map.shareToken}`;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleLeaveOrDelete = async () => {
    if (!map) return;
    const isOwner = map.createdBy === currentUser.uid;
    await leaveOrDeleteMap(map.id, currentUser.uid, isOwner).catch(console.error);
    if (listMode) {
      setInternalMapId(null);
    } else {
      onClose();
    }
  };

  const handleRemoveMember = async (uid: string) => {
    if (!map) return;
    await removeMemberFromMap(map.id, uid).catch(console.error);
  };

  const handleAddPackingList = async (localList: PackingList) => {
    if (!map) return;
    const shared: SharedPackingList = {
      id: localList.id,
      name: localList.name,
      categories: localList.categories,
      createdAt: localList.createdAt,
      updatedAt: Date.now(),
      createdBy: currentUser.uid,
      creatorName: currentUser.displayName || currentUser.email || 'Ukjent',
    };
    await addPackingListToMap(map.id, shared).catch(console.error);
    setShowPackingPicker(false);
  };

  const handleRemovePackingList = async (listId: string) => {
    if (!map) return;
    await removePackingListFromMap(map.id, listId).catch(console.error);
  };

  const handleTogglePackingItem = async (listId: string, catId: string, itemId: string) => {
    if (!map?.packingLists?.[listId]) return;
    const list = map.packingLists[listId];
    const updated: SharedPackingList = {
      ...list,
      categories: list.categories.map(cat =>
        cat.id !== catId ? cat : {
          ...cat,
          items: cat.items.map(item =>
            item.id !== itemId ? item : { ...item, checked: !item.checked }
          ),
        }
      ),
    };
    await updatePackingListInMap(map.id, listId, updated).catch(console.error);
  };

  const handleBackToList = () => {
    setInternalMapId(null);
    setShowCreateForm(false);
    setShowMenu(false);
    setShowSpotSearch(false);
    setShowRouteSearch(false);
    setShowInvite(false);
    setShowPackingPicker(false);
    setExpandedPackingId(null);
  };

  // Hydrering av delte plasser som ikke ligger i den innlastede `spots`-lista.
  // `spots` inneholder bare det som er lastet for gjeldende kartutsnitt, så en
  // plass et annet medlem har lagt til (eller en kommersiell campingplass) ville
  // ellers falle stille bort i `spots.find(...)` under. Vi henter de manglende
  // id-ene fra Firestore via fetchSpotsByIds (dekker spots + cached_spots).
  const [hydratedSpots, setHydratedSpots] = useState<CampingSpot[]>([]);
  const requestedSpotIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!map) return;
    const known = new Set(spots.map(s => s.id));
    const toFetch = map.spotIds.filter(id => !known.has(id) && !requestedSpotIdsRef.current.has(id));
    if (toFetch.length === 0) return;
    toFetch.forEach(id => requestedSpotIdsRef.current.add(id));

    let cancelled = false;
    (async () => {
      const merged = await fetchSpotsByIds(toFetch).catch(() => [] as CampingSpot[]);
      if (cancelled) return;
      if (merged.length === 0) return;
      setHydratedSpots(prev => {
        const byId = new Map(prev.map(s => [s.id, s]));
        for (const s of merged) byId.set(s.id, s);
        return Array.from(byId.values());
      });
    })();
    return () => { cancelled = true; };
  }, [map?.id, map?.spotIds, spots]);

  // Spots in the map — foretrekk innlastet `spots`, fall tilbake på hydrerte.
  const mapSpots = useMemo(() => {
    if (!map) return [] as CampingSpot[];
    const byId = new Map<string, CampingSpot>();
    for (const s of spots) byId.set(s.id, s);
    for (const s of hydratedSpots) if (!byId.has(s.id)) byId.set(s.id, s);
    return map.spotIds.map(id => byId.get(id)).filter(Boolean) as CampingSpot[];
  }, [map?.spotIds, spots, hydratedSpots]);

  // Kommune/fylke/land-etiketter for plasslisten. Land vises kun når plassene
  // spenner over flere land (f.eks. både Norge og Sverige).
  const spotLocationLabels = useMemo(() => buildSpotLocationLabels(mapSpots), [mapSpots]);

  // Spot search results
  const searchResults = spotSearch.trim().length >= 2
    ? spots
        .filter(s => !map?.spotIds.includes(s.id) && s.name.toLowerCase().includes(spotSearch.toLowerCase()))
        .slice(0, 8)
    : [];

  // Routes in the map
  const mapRoutes = map
    ? (map.routeIds || []).map(id => routes.find(r => r.id === id)).filter(Boolean) as GpxRoute[]
    : [];

  // Route search results
  const routeSearchResults = routeSearch.trim().length >= 2
    ? routes
        .filter(r => !(map?.routeIds || []).includes(r.id) && r.name.toLowerCase().includes(routeSearch.toLowerCase()))
        .slice(0, 8)
    : [];

  // Packing lists in the map
  const mapPackingLists = useMemo(() => {
    if (!map?.packingLists) return [];
    return Object.values(map.packingLists);
  }, [map?.packingLists]);

  // Local packing lists for picker (exclude already added ones)
  const localPackingLists = useMemo(() => {
    if (!showPackingPicker) return [];
    const local = loadPackingLists().filter(l => !l.archived);
    const existingIds = new Set(mapPackingLists.map(pl => pl.id));
    return local.filter(l => !existingIds.has(l.id));
  }, [showPackingPicker, mapPackingLists]);

  const handlePickMap = async (targetMapId: string) => {
    setSelectedMapForSpot(targetMapId);
    if (addToMapSpotId) {
      await addSpotToMap(targetMapId, addToMapSpotId).catch(console.error);
    } else if (addToMapRouteId) {
      await addRouteToMap(targetMapId, addToMapRouteId).catch(console.error);
    }
    setAddedToMap(targetMapId);
    setTimeout(() => onClose(), 800);
  };

  const isOwner = map ? map.createdBy === currentUser.uid : false;
  const isInDetailFromList = listMode && !!internalMapId && !!map;

  const content = (
      <div className={inline
        ? "flex flex-col overflow-hidden flex-1"
        : "relative w-full sm:max-w-md bg-[var(--bg-surface)] rounded-t-3xl sm:rounded-3xl border border-[var(--border-secondary)] shadow-2xl max-h-[90vh] flex flex-col overflow-hidden"
      }>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            {(isInDetailFromList || showCreateForm) && (
              <button onClick={handleBackToList} className="p-1 -ml-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                <ChevronLeft size={18} />
              </button>
            )}
            {!isListMode && !isInDetailFromList && !showCreateForm && !isPickMapMode && !isCreateMode && map && (
              <button onClick={onClose} className="p-1 -ml-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                <ChevronLeft size={18} />
              </button>
            )}
            <h2 className="text-[13px] font-black text-[var(--text-primary)]">
              {isListMode
                ? 'Mine mapper'
                : isPickMapMode
                ? 'Velg mappe'
                : (isCreateMode || showCreateForm)
                ? 'Ny samarbeidsmappe'
                : map?.name}
            </h2>
          </div>
          <div className="flex items-center gap-1">
            {!isCreateMode && !isPickMapMode && !isListMode && !showCreateForm && map && (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-xl hover:bg-[var(--bg-surface-alt)]"
                >
                  <MoreVertical size={16} />
                </button>
                {showMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-[var(--bg-input)] rounded-xl border border-[var(--border-secondary)] shadow-xl overflow-hidden z-10 min-w-[160px]">
                    {isOwner ? (
                      <button
                        onClick={() => { setShowMenu(false); handleLeaveOrDelete(); }}
                        className="w-full flex items-center gap-2 px-4 py-3 text-[12px] text-red-400 hover:bg-[var(--bg-surface-alt)]"
                      >
                        <Trash2 size={13} />
                        {t("auto.collaborative.collaborativemapmoda.slett_mappe", "Slett mappe")}
                      </button>
                    ) : (
                      <button
                        onClick={() => { setShowMenu(false); handleLeaveOrDelete(); }}
                        className="w-full flex items-center gap-2 px-4 py-3 text-[12px] text-red-400 hover:bg-[var(--bg-surface-alt)]"
                      >
                        <LogOut size={13} />
                        {t("auto.collaborative.collaborativemapmoda.forlat_mappe", "Forlat mappe")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <button onClick={onClose} className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-xl hover:bg-[var(--bg-surface-alt)]">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-5">

          {/* === LIST MODE === */}
          {isListMode && (
            <div className="space-y-3">
              {isPaidUser ? (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 active:scale-95 transition-all"
                >
                  <Plus size={15} />
                  <span className="text-tiny font-black uppercase tracking-widest">{t("auto.collaborative.collaborativemapmoda.ny_mappe", "Ny mappe")}</span>
                </button>
              ) : (
                <div className="flex items-center justify-center gap-2 py-3 rounded-2xl border bg-[var(--bg-surface-alt)] border-[var(--border-primary)] text-[var(--text-muted)]">
                  <Lock size={13} className="text-amber-500" />
                  <span className="text-tiny font-black uppercase tracking-widest">{t("auto.collaborative.collaborativemapmoda.ny_mappe_premium", "Ny mappe (premium)")}</span>
                </div>
              )}
              {maps.length === 0 ? (
                <div className="bg-[var(--bg-surface-alt)] rounded-2xl p-6 border border-[var(--border-primary)] text-center">
                  <p className="text-tiny text-[var(--text-muted)] leading-relaxed">{t("auto.collaborative.collaborativemapmoda.ingen_mapper_enna", "Ingen mapper ennå.")}</p>
                  <p className="text-2xs text-[var(--text-muted)] mt-1">
                    {isPaidUser
                      ? 'Opprett din første samarbeidsmappe ovenfor.'
                      : 'Oppgrader til premium for å opprette samarbeidsmapper.'}
                  </p>
                </div>
              ) : (
                maps.map(m => {
                  const mIsOwner = m.createdBy === currentUser.uid;
                  const memberNames = m.members.map(mem => mem.displayName).join(', ');
                  return (
                    <button
                      key={m.id}
                      onClick={() => setInternalMapId(m.id)}
                      className="w-full flex items-center justify-between p-3 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-primary)] hover:bg-white/8 active:scale-[0.98] transition-all text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-xl bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                          <MapIcon size={14} className="text-sky-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[12px] font-bold text-[var(--text-primary)] truncate">{m.name}</p>
                          <p className="text-3xs text-[var(--text-muted)] truncate">{memberNames}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-0.5 flex-shrink-0 ml-2">
                        <span className="text-2xs font-black text-[var(--text-secondary)]">
                          {m.spotIds.length} {m.spotIds.length === 1 ? 'plass' : 'plasser'}
                          {(m.routeIds?.length || 0) > 0 && ` · ${m.routeIds.length} ${m.routeIds.length === 1 ? 'rute' : 'ruter'}`}
                        </span>
                        <span className="text-3xs text-[var(--text-muted)]">
                          {mIsOwner ? 'Eier' : 'Redaktør'}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}

          {/* === PICK MAP MODE === */}
          {isPickMapMode && (
            <div className="space-y-2">
              <p className="text-2xs text-[var(--text-muted)]">
                {addToMapRouteId ? 'Velg hvilken mappe du vil legge ruten til i.' : 'Velg hvilken mappe du vil legge plassen til i.'}
              </p>
              {maps.length === 0 && (
                <p className="text-tiny text-[var(--text-muted)] text-center py-4">
                  {t("auto.collaborative.collaborativemapmoda.du_har_ingen_mapper_enna", "Du har ingen mapper ennå.")}
                </p>
              )}
              {maps.map(m => (
                <button
                  key={m.id}
                  onClick={() => handlePickMap(m.id)}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                    addedToMap === m.id
                      ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                      : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] hover:bg-white/8 active:scale-[0.98]'
                  }`}
                >
                  <span className="text-[12px] font-bold text-[var(--text-primary)]">{m.name}</span>
                  {addedToMap === m.id && <Check size={14} className="text-emerald-400" />}
                  {selectedMapForSpot === m.id && addedToMap !== m.id && (
                    <span className="text-2xs text-[var(--text-muted)]">{t("auto.collaborative.collaborativemapmoda.legger_til", "Legger til...")}</span>
                  )}
                </button>
              ))}

              {/* Create new folder inline */}
              {showCreateInPick ? (
                <div className="space-y-2 p-3 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-secondary)]">
                  <input
                    type="text"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder={t("auto.collaborative.collaborativemapmoda.placeholder.mappenavn", "Mappenavn")}
                    className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl px-3 py-2.5 text-[12px] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-emerald-500/50"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowCreateInPick(false); setNewName(''); }}
                      className="flex-1 py-2 rounded-xl text-tiny font-bold text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      {t("auto.collaborative.collaborativemapmoda.avbryt", "Avbryt")}
                    </button>
                    <button
                      onClick={handleCreate}
                      disabled={!newName.trim() || creating}
                      className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-xl text-tiny font-black text-white transition-colors disabled:opacity-50"
                    >
                      {creating ? t('collaborative.creating') : t('collaborative.create')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateInPick(true)}
                  className="w-full flex items-center justify-center gap-1.5 p-3 rounded-xl border border-dashed border-[var(--border-secondary)] hover:border-emerald-500/30 hover:bg-emerald-500/5 text-[var(--text-secondary)] hover:text-emerald-400 transition-all"
                >
                  <Plus size={14} />
                  <span className="text-tiny font-bold">{t("auto.collaborative.collaborativemapmoda.ny_mappe", "Ny mappe")}</span>
                </button>
              )}
            </div>
          )}

          {/* === CREATE MODE === */}
          {(isCreateMode || showCreateForm) && !isPickMapMode && !isListMode && (
            <div className="space-y-3">
              <div>
                <label className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-1.5 block">
                  {t("auto.collaborative.collaborativemapmoda.navn", "Navn")}
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder={t("auto.collaborative.collaborativemapmoda.placeholder.f_eks_sommertur_2026", "F.eks. Sommertur 2026")}
                  className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl px-4 py-3 text-[13px] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-emerald-500/50"
                  autoFocus
                  maxLength={60}
                />
              </div>
              <div>
                <label className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-1.5 block">
                  {t("auto.collaborative.collaborativemapmoda.beskrivelse_valgfri", "Beskrivelse (valgfri)")}
                </label>
                <input
                  type="text"
                  value={newDesc}
                  onChange={e => setNewDesc(e.target.value)}
                  placeholder={t("auto.collaborative.collaborativemapmoda.placeholder.f_eks_favorittplassene_vare", "F.eks. Favorittplassene våre")}
                  className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl px-4 py-3 text-[13px] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-emerald-500/50"
                  maxLength={120}
                />
              </div>
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || creating}
                className="w-full py-3.5 bg-emerald-500 text-white text-[12px] font-black uppercase tracking-widest rounded-2xl disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-400 active:scale-95 transition-all"
              >
                {creating ? t('collaborative.creating') : t('collaborative.create_folder')}
              </button>
            </div>
          )}

          {/* === VIEW / EDIT MODE === */}
          {!isCreateMode && !isPickMapMode && !isListMode && !showCreateForm && map && (
            <>
              {/* Medlemmer — shown first for visibility */}
              <div>
                <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-2">
                  Medlemmer ({map.members.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {map.members.map(mem => (
                    <div
                      key={mem.uid}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[var(--bg-surface-alt)] rounded-full border border-[var(--border-primary)]"
                    >
                      {mem.photoURL ? (
                        <img src={mem.photoURL} className="w-5 h-5 rounded-full flex-shrink-0" alt="" />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-[var(--bg-input)] flex items-center justify-center flex-shrink-0">
                          <Users size={9} className="text-[var(--text-secondary)]" />
                        </div>
                      )}
                      <span className="text-2xs font-bold text-[var(--text-primary)]">{mem.displayName}</span>
                      <span className="text-4xs text-[var(--text-muted)]">
                        {mem.role === 'owner' ? 'Eier' : ''}
                      </span>
                      {isOwner && mem.uid !== currentUser.uid && (
                        <button
                          onClick={() => handleRemoveMember(mem.uid)}
                          className="p-0.5 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                          title={t("auto.collaborative.collaborativemapmoda.title.fjern_fra_mappe", "Fjern fra mappe")}
                        >
                          <X size={10} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {pendingInvites.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {pendingInvites.map(email => (
                      <div
                        key={email}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[var(--bg-surface-alt)] rounded-full border border-dashed border-[var(--border-primary)] opacity-70"
                        title={t("auto.collaborative.collaborativemapmoda.venter_pa_at_de_lager_konto", "Venter på at de lager konto — får tilgang automatisk")}
                      >
                        <Mail size={11} className="text-[var(--text-muted)] flex-shrink-0" />
                        <span className="text-2xs text-[var(--text-secondary)] truncate max-w-[160px]">{email}</span>
                        <span className="text-4xs text-amber-400">{t("auto.collaborative.collaborativemapmoda.venter", "venter")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Chat — kun relevant når mappa har mer enn ett medlem */}
              {map.members.length > 1 && (
              <div>
                <button
                  onClick={() => setShowChat(v => !v)}
                  className="w-full flex items-center justify-between mb-2"
                >
                  <span className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] flex items-center gap-1.5">
                    <MessageCircle size={12} /> {t("auto.collaborative.collaborativemapmoda.chat", "Chat")}
                    {!showChat && hasUnreadCollabChat(map, currentUser.uid) && (
                      <span className="w-2 h-2 rounded-full bg-sky-400" />
                    )}
                  </span>
                  {showChat ? <ChevronDown size={14} className="text-[var(--text-muted)]" /> : <ChevronRight size={14} className="text-[var(--text-muted)]" />}
                </button>
                {showChat && (
                  <div className="h-80 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] p-2.5">
                    <CollabChatPanel
                      map={map}
                      currentUser={currentUser}
                      spots={spots}
                      routes={routes}
                      onOpenSpot={id => { const s = spots.find(x => x.id === id); if (s) onSelectSpot?.(s, map!.id); }}
                      onOpenRoute={id => onSelectRoute?.(id, map!.id)}
                      onOpenPackingList={id => setExpandedPackingId(id)}
                    />
                  </div>
                )}
              </div>
              )}

              {/* Plasser */}
              <div>
                <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-2">
                  Plasser ({mapSpots.length})
                </p>

                {mapSpots.length === 0 && !showSpotSearch && (
                  <p className="text-tiny text-[var(--text-muted)] mb-2">{t("auto.collaborative.collaborativemapmoda.ingen_plasser_enna", "Ingen plasser ennå.")}</p>
                )}

                <div className="space-y-1.5">
                  {mapSpots.map(s => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between p-2.5 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-primary)]"
                    >
                      <button
                        onClick={() => onSelectSpot?.(s, map!.id)}
                        className="flex items-center gap-2 min-w-0 text-left group"
                      >
                        <MapPin size={12} className="text-emerald-500 flex-shrink-0" />
                        <span className="min-w-0">
                          <span className="block text-tiny font-bold text-[var(--text-primary)] truncate group-hover:text-emerald-400 transition-colors">{s.name}</span>
                          {spotLocationLabels[s.id] && (
                            <span className="block text-2xs text-[var(--text-muted)] truncate">{spotLocationLabels[s.id]}</span>
                          )}
                        </span>
                      </button>
                      <button
                        onClick={() => handleRemoveSpot(s.id)}
                        className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors ml-2 flex-shrink-0"
                        title={t("auto.collaborative.collaborativemapmoda.title.fjern_fra_mappe", "Fjern fra mappe")}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>

                {showSpotSearch ? (
                  <div className="mt-2 space-y-1.5">
                    <div className="relative">
                      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                      <input
                        type="text"
                        value={spotSearch}
                        onChange={e => setSpotSearch(e.target.value)}
                        placeholder={t("auto.collaborative.collaborativemapmoda.placeholder.sok_etter_plass", "Søk etter plass...")}
                        className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl pl-8 pr-4 py-2.5 text-[12px] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-emerald-500/50"
                        autoFocus
                      />
                    </div>
                    {searchResults.map(s => (
                      <button
                        key={s.id}
                        onClick={() => handleAddSpot(s.id)}
                        className="w-full flex items-center gap-2 p-2.5 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-primary)] hover:bg-white/8 text-left"
                      >
                        <MapPin size={11} className="text-[var(--text-muted)] flex-shrink-0" />
                        <span className="text-tiny text-[var(--text-primary)] truncate">{s.name}</span>
                      </button>
                    ))}
                    {spotSearch.trim().length >= 2 && searchResults.length === 0 && (
                      <p className="text-2xs text-[var(--text-muted)] text-center py-1">
                        {t("auto.collaborative.collaborativemapmoda.ingen_treff", "Ingen treff")}
                      </p>
                    )}
                    <button
                      onClick={() => { setShowSpotSearch(false); setSpotSearch(''); }}
                      className="text-2xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] mt-1"
                    >
                      {t("auto.collaborative.collaborativemapmoda.avbryt", "Avbryt")}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowSpotSearch(true)}
                    className="mt-2 flex items-center gap-1.5 text-tiny text-emerald-400 hover:text-emerald-300"
                  >
                    <Plus size={13} />
                    {t("auto.collaborative.collaborativemapmoda.legg_til_plass", "Legg til plass")}
                  </button>
                )}
              </div>

              {/* Ruter */}
              <div>
                <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-2">
                  Ruter ({mapRoutes.length})
                </p>

                {mapRoutes.length === 0 && !showRouteSearch && (
                  <p className="text-tiny text-[var(--text-muted)] mb-2">{t("auto.collaborative.collaborativemapmoda.ingen_ruter_enna", "Ingen ruter ennå.")}</p>
                )}

                <div className="space-y-1.5">
                  {mapRoutes.map(r => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between p-2.5 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-primary)]"
                    >
                      <button
                        onClick={() => onSelectRoute?.(r.id, map!.id)}
                        className="flex items-center gap-2 min-w-0 text-left group"
                      >
                        <Route size={12} className="text-blue-400 flex-shrink-0" />
                        <span className="text-tiny font-bold text-[var(--text-primary)] truncate group-hover:text-blue-400 transition-colors">{r.name}</span>
                      </button>
                      <button
                        onClick={() => handleRemoveRoute(r.id)}
                        className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors ml-2 flex-shrink-0"
                        title={t("auto.collaborative.collaborativemapmoda.title.fjern_fra_mappe", "Fjern fra mappe")}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>

                {showRouteSearch ? (
                  <div className="mt-2 space-y-1.5">
                    <div className="relative">
                      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                      <input
                        type="text"
                        value={routeSearch}
                        onChange={e => setRouteSearch(e.target.value)}
                        placeholder={t("auto.collaborative.collaborativemapmoda.placeholder.sok_etter_rute", "Søk etter rute...")}
                        className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl pl-8 pr-4 py-2.5 text-[12px] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-blue-500/50"
                        autoFocus
                      />
                    </div>
                    {routeSearchResults.map(r => (
                      <button
                        key={r.id}
                        onClick={() => handleAddRoute(r.id)}
                        className="w-full flex items-center gap-2 p-2.5 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-primary)] hover:bg-white/8 text-left"
                      >
                        <Route size={11} className="text-[var(--text-muted)] flex-shrink-0" />
                        <span className="text-tiny text-[var(--text-primary)] truncate">{r.name}</span>
                      </button>
                    ))}
                    {routeSearch.trim().length >= 2 && routeSearchResults.length === 0 && (
                      <p className="text-2xs text-[var(--text-muted)] text-center py-1">
                        {t("auto.collaborative.collaborativemapmoda.ingen_treff", "Ingen treff")}
                      </p>
                    )}
                    <button
                      onClick={() => { setShowRouteSearch(false); setRouteSearch(''); }}
                      className="text-2xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] mt-1"
                    >
                      {t("auto.collaborative.collaborativemapmoda.avbryt", "Avbryt")}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowRouteSearch(true)}
                    className="mt-2 flex items-center gap-1.5 text-tiny text-blue-400 hover:text-blue-300"
                  >
                    <Plus size={13} />
                    {t("auto.collaborative.collaborativemapmoda.legg_til_rute", "Legg til rute")}
                  </button>
                )}
              </div>

              {/* Pakkelister */}
              <div>
                <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-2">
                  Pakkelister ({mapPackingLists.length})
                </p>

                {mapPackingLists.length === 0 && !showPackingPicker && (
                  <p className="text-tiny text-[var(--text-muted)] mb-2">{t("auto.collaborative.collaborativemapmoda.ingen_pakkelister_enna", "Ingen pakkelister ennå.")}</p>
                )}

                <div className="space-y-1.5">
                  {mapPackingLists.map(pl => {
                    const total = pl.categories.reduce((a, c) => a + c.items.length, 0);
                    const checked = pl.categories.reduce((a, c) => a + c.items.filter(i => i.checked).length, 0);
                    const pct = total > 0 ? Math.round((checked / total) * 100) : 0;
                    const isExpanded = expandedPackingId === pl.id;
                    return (
                      <div key={pl.id}>
                        <div className="flex items-center justify-between p-2.5 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-primary)]">
                          <button
                            onClick={() => setExpandedPackingId(isExpanded ? null : pl.id)}
                            className="flex items-center gap-2 min-w-0 text-left flex-1 group"
                          >
                            <ClipboardList size={12} className="text-emerald-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="text-tiny font-bold text-[var(--text-primary)] truncate block">{pl.name}</span>
                              <div className="flex items-center gap-2 mt-0.5">
                                <div className="flex-1 h-1 bg-[var(--bg-input)] rounded-full overflow-hidden">
                                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-3xs font-bold text-[var(--text-muted)] shrink-0">{checked}/{total}</span>
                              </div>
                            </div>
                            {isExpanded
                              ? <ChevronDown size={12} className="text-[var(--text-muted)] flex-shrink-0" />
                              : <ChevronRight size={12} className="text-[var(--text-muted)] flex-shrink-0" />
                            }
                          </button>
                          <button
                            onClick={() => handleRemovePackingList(pl.id)}
                            className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors ml-1 flex-shrink-0"
                            title={t("auto.collaborative.collaborativemapmoda.title.fjern_fra_mappe", "Fjern fra mappe")}
                          >
                            <X size={13} />
                          </button>
                        </div>
                        {isExpanded && (
                          <div className="ml-4 mt-1 space-y-2">
                            {pl.categories.map(cat => {
                              const catChecked = cat.items.filter(i => i.checked).length;
                              return (
                                <div key={cat.id}>
                                  <p className="text-3xs font-bold text-[var(--text-muted)] uppercase tracking-wider mb-1">
                                    {cat.emoji} {cat.label} ({catChecked}/{cat.items.length})
                                  </p>
                                  <div className="space-y-0.5">
                                    {cat.items.map(item => (
                                      <button
                                        key={item.id}
                                        onClick={() => handleTogglePackingItem(pl.id, cat.id, item.id)}
                                        className="w-full flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-[var(--bg-surface-alt)] transition-colors text-left"
                                      >
                                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                                          item.checked
                                            ? 'bg-emerald-500 border-emerald-500'
                                            : 'border-[var(--border-secondary)]'
                                        }`}>
                                          {item.checked && <Check size={9} className="text-white" />}
                                        </div>
                                        <span className={`text-tiny ${item.checked ? 'line-through text-[var(--text-muted)]' : 'text-[var(--text-primary)]'}`}>
                                          {item.label}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {showPackingPicker ? (
                  <div className="mt-2 space-y-1.5">
                    {localPackingLists.length === 0 ? (
                      <p className="text-2xs text-[var(--text-muted)] text-center py-2">
                        {loadPackingLists().filter(l => !l.archived).length === 0
                          ? 'Du har ingen pakkelister. Opprett en i Pakke-fanen først.'
                          : 'Alle dine pakkelister er allerede lagt til.'}
                      </p>
                    ) : (
                      localPackingLists.map(l => {
                        const t = l.categories.reduce((a, c) => a + c.items.length, 0);
                        const ch = l.categories.reduce((a, c) => a + c.items.filter(i => i.checked).length, 0);
                        return (
                          <button
                            key={l.id}
                            onClick={() => handleAddPackingList(l)}
                            className="w-full flex items-center gap-2 p-2.5 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-primary)] hover:bg-white/8 text-left"
                          >
                            <ClipboardList size={11} className="text-emerald-400 flex-shrink-0" />
                            <span className="text-tiny text-[var(--text-primary)] truncate flex-1">{l.name}</span>
                            <span className="text-3xs text-[var(--text-muted)] shrink-0">{ch}/{t}</span>
                          </button>
                        );
                      })
                    )}
                    <button
                      onClick={() => setShowPackingPicker(false)}
                      className="text-2xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] mt-1"
                    >
                      {t("auto.collaborative.collaborativemapmoda.avbryt", "Avbryt")}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowPackingPicker(true)}
                    className="mt-2 flex items-center gap-1.5 text-tiny text-emerald-400 hover:text-emerald-300"
                  >
                    <Plus size={13} />
                    {t("auto.collaborative.collaborativemapmoda.legg_til_pakkeliste", "Legg til pakkeliste")}
                  </button>
                )}
              </div>

              {/* Deling */}
              <div>
                <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-2">
                  {t("auto.collaborative.collaborativemapmoda.del_mappe", "Del mappe")}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleCopyLink}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] hover:bg-white/8 transition-all active:scale-95"
                  >
                    {linkCopied ? (
                      <><Check size={13} className="text-emerald-400" /><span className="text-tiny text-emerald-400 font-bold">{t("auto.collaborative.collaborativemapmoda.kopiert", "Kopiert!")}</span></>
                    ) : (
                      <><Copy size={13} className="text-[var(--text-secondary)]" /><span className="text-tiny text-[var(--text-secondary)]">{t("auto.collaborative.collaborativemapmoda.kopier_lenke", "Kopier lenke")}</span></>
                    )}
                  </button>
                  <button
                    onClick={() => setShowInvite(!showInvite)}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] hover:bg-white/8 transition-all active:scale-95"
                  >
                    <Mail size={13} className="text-[var(--text-secondary)]" />
                    <span className="text-tiny text-[var(--text-secondary)]">{t("auto.collaborative.collaborativemapmoda.inviter", "Inviter")}</span>
                  </button>
                </div>

                {showInvite && (
                  <div className="mt-2 space-y-2">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Mail size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={e => { setInviteEmail(e.target.value); setInviteStatus('idle'); }}
                          onKeyDown={e => e.key === 'Enter' && handleInvite()}
                          placeholder={t("auto.collaborative.collaborativemapmoda.placeholder.e_postadresse", "E-postadresse")}
                          className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl pl-8 pr-3 py-2.5 text-[12px] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-sky-500/50"
                        />
                      </div>
                      <button
                        onClick={handleInvite}
                        disabled={inviteStatus === 'loading' || inviteStatus === 'sending_email' || !inviteEmail.trim()}
                        className="px-4 py-2.5 bg-sky-500/20 border border-sky-500/30 text-sky-400 rounded-xl text-tiny font-bold disabled:opacity-40 hover:bg-sky-500/30 transition-all"
                      >
                        {inviteStatus === 'loading' ? '...' : 'Send'}
                      </button>
                    </div>
                    {inviteStatus === 'ok' && (
                      <p className="text-2xs text-emerald-400 flex items-center gap-1">
                        <Check size={11} /> {t("auto.collaborative.collaborativemapmoda.bruker_lagt_til_i_mappen", "Bruker lagt til i mappen!")}
                      </p>
                    )}
                    {(inviteStatus === 'not_found' || inviteStatus === 'sending_email') && (
                      <div className="p-2.5 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] space-y-2">
                        <p className="text-2xs text-[var(--text-secondary)]">
                          {t("auto.collaborative.collaborativemapmoda.ingen_bruker_send_epost_sporsmal", "Ingen bruker har denne e-posten ennå. Vil du sende en invitasjon på e-post? De får automatisk tilgang når de oppretter konto.")}
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={handleSendEmailInvite}
                            disabled={inviteStatus === 'sending_email'}
                            className="flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-lg text-2xs font-bold disabled:opacity-40 hover:bg-emerald-500/30 transition-all"
                          >
                            <Mail size={11} /> {inviteStatus === 'sending_email' ? t("auto.collaborative.collaborativemapmoda.sender", "Sender …") : t("auto.collaborative.collaborativemapmoda.send_invitasjon_pa_epost", "Send invitasjon på e-post")}
                          </button>
                          <button
                            onClick={() => setInviteStatus('idle')}
                            disabled={inviteStatus === 'sending_email'}
                            className="px-3 py-2 text-2xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
                          >
                            {t("auto.collaborative.collaborativemapmoda.avbryt", "Avbryt")}
                          </button>
                        </div>
                      </div>
                    )}
                    {inviteStatus === 'invited' && (
                      <p className="text-2xs text-emerald-400 flex items-center gap-1">
                        <Mail size={11} /> {t("auto.collaborative.collaborativemapmoda.invitasjon_sendt_pa_epost", "Invitasjon sendt på e-post. De får tilgang automatisk når de lager konto.")}
                      </p>
                    )}
                    {inviteStatus === 'error' && (
                      <p className="text-2xs text-red-400">{t("auto.collaborative.collaborativemapmoda.kunne_ikke_sende_invitasjon", "Kunne ikke sende invitasjonen. Prøv igjen.")}</p>
                    )}
                    {inviteStatus === 'already_member' && (
                      <p className="text-2xs text-amber-400">{t("auto.collaborative.collaborativemapmoda.brukeren_er_allerede_med_i_map", "Brukeren er allerede med i mappen.")}</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
  );

  if (inline) return content;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      {content}
    </div>
  );
};

export default CollaborativeMapModal;
